/**
 * 成就：积分余额 + 调整积分、奖品、成就规则（开关 / 改奖励）、卡册、积分明细。参考 iOS Apps/Parent/Sources/Achieve/。
 * 子页面用查询参数：?view=cards / ?view=ledger。调试：?dev_sheet=reward（新奖品）/ adjust（调整积分）/ grant_card（发卡）/ card_detail（卡册里第一条记录）。
 */
import { ChevronLeft, ChevronRight, GalleryVerticalEnd, List, ScanLine, Star, WalletCards } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { type AchievementRule, api, didWrite, type Reward, run, select, shallowEqual, signed, upsert, useCalendar, useCurrentChild, useDevSheet, useStore } from "../../core";
import { AdjustPointsSheet } from "../../components/AdjustPointsSheet";
import { CardArtImage, CardPhoto } from "../../components/cards/CardPhotos";
import { CardDetailSheet, CardRecordRow } from "../../components/cards/CardRecord";
import { GrantCardSheet } from "../../components/cards/GrantCardSheet";
import { CardFace, ChildSwitcher, EmptyHint, FormCard, IconButton, Page, PageTitle, RowDivider, Switch } from "../../ui";
import "./achieve.css";
import { ruleSummary } from "./ruleText";
import { RewardEditSheet, RuleEditSheet } from "./sheets";

export function AchievePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const view = params.get("view");
  const back = () => (location.key !== "default" ? navigate(-1) : navigate("/achieve", { replace: true }));
  if (view === "cards") return <CardBook onBack={back} />;
  if (view === "ledger") return <Ledger onBack={back} />;
  return <AchieveHome onOpen={(v) => navigate(`/achieve?view=${v}`)} />;
}

function AchieveHome({ onOpen }: { onOpen: (v: "cards" | "ledger") => void }) {
  const child = useCurrentChild();
  const [adjusting, setAdjusting] = useDevSheet("adjust");
  const [granting, setGranting] = useDevSheet("grant_card");
  const [newReward, setNewReward] = useDevSheet("reward");
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [editingRule, setEditingRule] = useState<AchievementRule | null>(null);
  const cardCount = useStore((s) => (child ? Object.values(s.childCards).filter((c) => c.childId === child.id).length : 0));

  return (
    <Page gap={12}>
      <PageTitle trailing={<ChildSwitcher />}>成就</PageTitle>
      {child ? (
        <>
          <div className="achieve-balance">
            <div>
              <span>积分余额 · 卡牌 {cardCount} 张</span>
              <strong className="kp-num" data-testid="balance">
                {child.pointsBalance}
              </strong>
            </div>
            <div className="achieve-balance-actions">
              <button type="button" className="achieve-adjust-btn" onClick={() => setAdjusting(true)}>
                <ScanLine size={16} strokeWidth={2.6} />
                <span>调整积分</span>
              </button>
              <button type="button" className="achieve-adjust-btn is-card" onClick={() => setGranting(true)} data-testid="grant-card">
                <WalletCards size={16} strokeWidth={2.6} />
                <span>发一张卡</span>
              </button>
            </div>
          </div>
          <RewardsCard onNew={() => setNewReward(true)} onEdit={setEditingReward} />
          <RulesCard onEdit={setEditingRule} />
          <FormCard>
            <LinkRow icon={GalleryVerticalEnd} title={`${child.name}的卡册和成长记录`} onClick={() => onOpen("cards")} />
            <RowDivider />
            <LinkRow icon={List} title="积分明细" onClick={() => onOpen("ledger")} />
          </FormCard>
          <AdjustPointsSheet open={adjusting} onClose={() => setAdjusting(false)} childId={child.id} />
          <GrantCardSheet open={granting} onClose={() => setGranting(false)} child={child} />
        </>
      ) : (
        <EmptyHint icon={Star} text="先在「我的」里添加孩子" />
      )}
      <RewardEditSheet open={newReward || !!editingReward} reward={editingReward} onClose={() => (setNewReward(false), setEditingReward(null))} />
      <RuleEditSheet rule={editingRule} onClose={() => setEditingRule(null)} />
    </Page>
  );
}

function LinkRow({ icon: Icon, title, onClick }: { icon: typeof List; title: string; onClick: () => void }) {
  return (
    <button type="button" className="achieve-link" onClick={onClick}>
      <Icon size={17} strokeWidth={2.2} />
      <span>{title}</span>
      <ChevronRight size={15} strokeWidth={2.6} />
    </button>
  );
}

function RewardsCard({ onNew, onEdit }: { onNew: () => void; onEdit: (r: Reward) => void }) {
  const rewardsById = useStore((s) => s.rewards);
  const redemptions = useStore((s) => s.redemptions);
  const rewards = useMemo(
    () => Object.values(rewardsById).sort((a, b) => (a.enabled ? 0 : 1) - (b.enabled ? 0 : 1) || a.costPoints - b.costPoints),
    [rewardsById],
  );
  const stockText = (r: Reward) => {
    const pending = Object.values(redemptions).filter((x) => x.rewardId === r.id && x.status === "pending").length;
    if (pending > 0) return `${pending} 个申请待审批`;
    if (!r.enabled) return "已下架";
    return r.stock != null ? `库存 ${r.stock}` : "不限量";
  };
  return (
    <div className="achieve-card">
      <div className="achieve-card-head">
        <h2>奖品</h2>
        <button type="button" className="achieve-link-btn" onClick={onNew}>
          + 新奖品
        </button>
      </div>
      {rewards.length === 0 && <p className="achieve-empty">还没有奖品</p>}
      {rewards.map((r) => (
        <button key={r.id} type="button" className="achieve-reward" onClick={() => onEdit(r)} data-testid="reward-row">
          {r.imageAssetId && (
            <span className="achieve-reward-thumb" style={{ opacity: r.enabled ? 1 : 0.5 }}>
              <CardPhoto assetId={r.imageAssetId} size={34} />
            </span>
          )}
          <strong style={{ color: r.enabled ? undefined : "var(--kp-ink-muted)" }}>{r.name}</strong>
          <small>{stockText(r)}</small>
          <span className="kp-num">{r.costPoints}</span>
        </button>
      ))}
    </div>
  );
}

function RulesCard({ onEdit }: { onEdit: (r: AchievementRule) => void }) {
  const rulesById = useStore((s) => s.achievementRules);
  const rules = useMemo(() => Object.values(rulesById).sort((a, b) => (a.key ?? a.name).localeCompare(b.key ?? b.name)), [rulesById]);

  // 乐观更新：先改本地，失败时恢复
  const toggle = async (r: AchievementRule, on: boolean) => {
    upsert("achievementRules", { ...r, enabled: on });
    const res = await run(() => api.patch<AchievementRule>(`achievement-rules/${r.id}`, { enabled: on }));
    if (res) didWrite("achievementRules", res);
    else upsert("achievementRules", r);
  };

  return (
    <div className="achieve-card">
      <div className="achieve-card-head">
        <h2>成就规则</h2>
        <small>达成后自动加分，可开关、改分数</small>
      </div>
      {rules.length === 0 && <p className="achieve-empty">还没有成就规则</p>}
      {rules.map((r) => (
        <div key={r.id} className="achieve-rule" data-testid="rule-row">
          <button type="button" onClick={() => onEdit(r)}>
            <strong>{r.name}</strong>
            <small>{ruleSummary(r)}</small>
          </button>
          <Switch checked={r.enabled} onChange={(v) => void toggle(r, v)} />
        </div>
      ))}
    </div>
  );
}

function SubHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="achieve-back-header">
      <IconButton label="返回" onClick={onBack}>
        <ChevronLeft size={22} strokeWidth={2.6} />
      </IconButton>
      <h1>{title}</h1>
      <span style={{ width: 40 }} />
    </div>
  );
}

function CardBook({ onBack }: { onBack: () => void }) {
  const child = useCurrentChild();
  const seriesList = useStore(select.collectionSeries, shallowEqual);
  const cardsById = useStore((s) => s.cards);
  const owned = useStore((s) => select.ownedCardCounts(s, child?.id ?? ""), shallowEqual);
  const records = useStore((s) => select.cardRecords(s, child?.id ?? ""), shallowEqual);
  const [granting, setGranting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [opened, setOpened] = useDevOpen(records[0]?.record.id ?? null);
  return (
    <Page gap={16}>
      <SubHeader title={`${child?.name ?? ""}的卡册`} onBack={onBack} />
      <section className="achieve-series">
        <div className="achieve-series-head">
          <h2>成长记录</h2>
          <span className="kp-num">{records.length}</span>
          <button type="button" className="achieve-link-btn" onClick={() => setGranting(true)}>
            + 发一张卡
          </button>
        </div>
        {records.length === 0 && <p className="achieve-empty">还没有卡。孩子做了值得记住的事，就发一张卡记下来吧。</p>}
        <div className="achieve-records">
          {records.slice(0, showAll ? 500 : 6).map((r) => (
            <CardRecordRow key={r.record.id} record={r.record} card={r.card} onOpen={() => setOpened(r.record.id)} />
          ))}
        </div>
        {records.length > 6 && !showAll && (
          <button type="button" className="achieve-link-btn" onClick={() => setShowAll(true)}>
            查看全部 {records.length} 条
          </button>
        )}
      </section>
      {seriesList.map((s) => {
        const cards = Object.values(cardsById)
          .filter((c) => c.seriesId === s.id)
          .sort((a, b) => a.no - b.no);
        const got = cards.filter((c) => (owned[c.id] ?? 0) > 0).length;
        return (
          <section key={s.id} className="achieve-series">
            <div className="achieve-series-head">
              <h2>{s.name}</h2>
              <span className="kp-num">
                {got}/{cards.length}
              </span>
              <em>集齐 +{s.completionPoints}</em>
            </div>
            <div className="achieve-cards">
              {cards.map((c) => {
                const n = owned[c.id] ?? 0;
                const rec = n > 0 ? records.find((r) => r.card.id === c.id) : undefined;
                return rec ? (
                  <button key={c.id} type="button" className="achieve-card-btn" onClick={() => setOpened(rec.record.id)}>
                    <CardFace glyph={c.glyph} name={c.name} rarity={c.rarity} count={n} owned art={c.imageAssetId ? <CardArtImage assetId={c.imageAssetId} /> : undefined} />
                  </button>
                ) : (
                  <CardFace key={c.id} glyph={c.glyph} name={c.name} rarity={c.rarity} count={n} owned={n > 0} art={c.imageAssetId ? <CardArtImage assetId={c.imageAssetId} /> : undefined} />
                );
              })}
            </div>
          </section>
        );
      })}
      <GrantCardSheet open={granting} onClose={() => setGranting(false)} child={child ?? null} />
      <CardDetailSheet recordId={opened} onClose={() => setOpened(null)} />
    </Page>
  );
}

/** 调试 ?dev_sheet=card_detail：打开第一条记录 */
function useDevOpen(first: string | null): [string | null, (id: string | null) => void] {
  const [dev, setDev] = useDevSheet("card_detail");
  const [opened, setOpened] = useState<string | null>(null);
  return [opened ?? (dev ? first : null), (id) => (setDev(false), setOpened(id))];
}

function Ledger({ onBack }: { onBack: () => void }) {
  const child = useCurrentChild();
  const list = useStore((s) => select.ledgerOf(s, child?.id ?? ""), shallowEqual);
  const cal = useCalendar();
  return (
    <Page gap={8}>
      <SubHeader title="积分明细" onBack={onBack} />
      {list.length === 0 && <EmptyHint icon={List} text="还没有积分记录" />}
      {list.slice(0, 300).map((e) => (
        <div key={e.id} className="achieve-ledger-row">
          <div>
            <strong>{e.reason}</strong>
            <small>
              {cal.relativeDay(cal.dateString(e.createdAt))} {cal.timeString(e.createdAt)}
              {e.actorName ? ` · ${e.actorName}` : ""}
            </small>
          </div>
          <span className={`kp-num ${e.delta >= 0 ? "is-plus" : "is-minus"}`}>{signed(e.delta)}</span>
        </div>
      ))}
    </Page>
  );
}
